/**
 * ProjectIndicator -- compact "active project" chip + switcher menu.
 *
 * Lives in the global PlatformNav so every page (Hub, DesignGrid,
 * 8760, Exclusions, etc.) shows the user which project is currently
 * loaded, with a one-click switcher.
 *
 * The actual project picker (ProjectBrowser) lives inside the planner
 * route. Rather than duplicate that UI here, the indicator's switcher
 * navigates to /planner with ``?openProjectBrowser=1`` and the
 * planner auto-opens its browser. "+ New" appends ``&newProject=1``
 * which the planner uses to switch the browser into its create-form
 * tab. "Clear" wipes the localStorage keys (so the next project-aware
 * page sees no active project) and stays on the current page.
 *
 * Before performing any of these actions, the indicator consults the
 * global dirty registry (useDirtyFlag) and prompts the user with a
 * window.confirm if any page has unsaved changes -- so a planner
 * session with edits won't get silently abandoned.
 *
 * The PLANNER's own project chip (in App.tsx's blue header) stays as
 * is per the v1.9.7.x design decision to keep changes minimal; this
 * indicator is intentionally hidden on /planner to avoid showing two
 * chips at once.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getDirtyKeys,
  hasAnyDirty,
} from '../hooks/useDirtyFlag';
import {
  clearActiveProject,
  useActiveProject,
} from '../hooks/useActiveProject';
import { openProjectArtifactsModal } from '../PlatformShell';

interface ProjectIndicatorProps {
  /** Optional className for the outer wrapper -- lets the host
   *  control margins, dark/light mode etc. */
  className?: string;
}

/**
 * Confirm-before-discard helper. Returns true if the caller may
 * proceed (no dirty pages, OR user explicitly chose to discard).
 */
function confirmDiscardIfDirty(actionLabel: string): boolean {
  if (!hasAnyDirty()) return true;
  const dirtyList = getDirtyKeys()
    .map((k) => `  - ${k}`)
    .join('\n');
  const msg =
    `Unsaved changes in:\n${dirtyList}\n\n` +
    `${actionLabel} will discard these changes.\n\n` +
    `Click OK to discard and continue, or Cancel to keep editing ` +
    `(then Save in the affected tabs first).`;
  // window.confirm is the cheapest cross-browser modal; a fancier
  // dialog could come later but the semantics (block until user
  // chooses) match what we need for a navigation guard.
  // eslint-disable-next-line no-alert
  return window.confirm(msg);
}

const ProjectIndicator: React.FC<ProjectIndicatorProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, projectName, isSet } = useActiveProject();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // We hide the indicator on /planner (the planner has its own
  // project chip in its blue header), but the visibility check has
  // to happen AFTER the hooks below so we don't violate the
  // rules-of-hooks (an early return before useEffect/useCallback
  // would change the hook call order between renders -> ESLint
  // error + bugs).
  const hidden = location.pathname.startsWith('/planner');

  // Close on outside click + Escape so the dropdown doesn't trap.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const goToBrowser = useCallback((newProject: boolean) => {
    setOpen(false);
    if (!confirmDiscardIfDirty(newProject ? 'Creating a new project' : 'Switching projects')) {
      return;
    }
    const params = new URLSearchParams();
    params.set('openProjectBrowser', '1');
    if (newProject) params.set('newProject', '1');
    navigate(`/planner?${params.toString()}`);
  }, [navigate]);

  const handleSwitch = useCallback(() => goToBrowser(false), [goToBrowser]);
  const handleNew = useCallback(() => goToBrowser(true), [goToBrowser]);

  const handleClear = useCallback(() => {
    setOpen(false);
    if (!confirmDiscardIfDirty('Clearing the active project')) return;
    clearActiveProject();
    // Don't navigate -- the user might be on a project-less page
    // (Hub, CDF admin) and forcing them to /planner would be jarring.
    // Project-aware pages re-render via useActiveProject and show
    // their no-project empty state.
  }, []);

  if (hidden) return null;

  // Display formats: "Project: MTU" when set, or "No project" when
  // not. Truncate long names via CSS so the indicator doesn't blow
  // up the nav bar on very wide projects.
  const labelMain = isSet ? projectName : 'No project';
  const labelSub = isSet
    ? `id: ${projectId.slice(0, 8)}…`
    : 'click to load or create';

  return (
    <div ref={wrapperRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs border transition-colors min-w-0 w-full max-w-full overflow-hidden ${
          isSet
            ? 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100'
            : 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
        }`}
        title={
          isSet
            ? `Active project: ${projectName} (${projectId.slice(0, 8)}…). Click for switch / new / clear.`
            : 'No project loaded. Click to switch, create new, or stay project-less.'
        }
        data-tutorial="project-indicator"
      >
        <span className="text-base leading-none shrink-0">{isSet ? '📁' : '○'}</span>
        <span className="flex flex-col items-start leading-tight min-w-0 flex-1">
          <span className="font-semibold truncate w-full">{labelMain}</span>
          <span className="text-[10px] text-gray-500 truncate w-full">{labelSub}</span>
        </span>
        <span className="text-gray-400 text-[10px] shrink-0">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg z-50 py-1 text-sm">
          {isSet && (
            <div className="px-3 py-2 border-b text-xs text-gray-600">
              <div className="font-medium text-gray-900">{projectName}</div>
              <div className="font-mono text-[10px] text-gray-400 truncate">{projectId}</div>
            </div>
          )}
          {isSet && (
            <button
              type="button"
              onClick={() => { setOpen(false); openProjectArtifactsModal(); }}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-emerald-700"
            >
              <span className="text-base leading-none">📋</span>
              <span>View saved artifacts…</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSwitch}
            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="text-base leading-none">🔄</span>
            <span>{isSet ? 'Switch project…' : 'Pick a project…'}</span>
          </button>
          <button
            type="button"
            onClick={handleNew}
            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="text-base leading-none">➕</span>
            <span>New project…</span>
          </button>
          {isSet && (
            <button
              type="button"
              onClick={handleClear}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-amber-700"
            >
              <span className="text-base leading-none">⊘</span>
              <span>Clear active project</span>
            </button>
          )}
          <div className="border-t mt-1 px-3 py-1.5 text-[10px] text-gray-400 leading-snug">
            The active project is shared across Hub, Design Grid,
            8760, Exclusions and the planner. Pages that need a
            project will pick it up automatically.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectIndicator;
